import devLog from './devLog';
/**
 * SILENT UPDATE MANAGER - V25.3.0
 * 
 * Strategy: Silent Background Update + Activate on Next Safe Navigation
 * 
 * RULES:
 * 1. NO window.location.reload() - EVER
 * 2. NO blinking or visual interruption
 * 3. NO popup or notification
 * 4. NO interruption to streaming
 * 5. Activate ONLY when safe (idle, navigation, no streaming)
 */

// ═══════════════════════════════════════════════════════════════════════════════
// STATE TRACKING
// ═══════════════════════════════════════════════════════════════════════════════

const state = {
  updateReady: false,
  newVersion: null,
  isStreaming: false,
  isGenerating: false,
  isRefining: false,
  pendingApiCalls: 0,
  lastActivity: Date.now(),
  activationAttempted: false,
  currentVersion: null,
};

// Idle timeout (30 seconds of inactivity)
const IDLE_TIMEOUT = 30000;

// Version check interval (5 minutes)
const VERSION_CHECK_INTERVAL = 5 * 60 * 1000;

// ═══════════════════════════════════════════════════════════════════════════════
// 1️⃣ BACKGROUND UPDATE DETECTION
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Check for new version in background
 * Downloads new assets silently without activating
 */
export const checkForUpdate = async () => {
  try {
    const response = await fetch('/api/version', { 
      cache: 'no-store',
      headers: { 'Cache-Control': 'no-cache' }
    });
    
    if (!response.ok) return;
    
    const data = await response.json();
    const serverVersion = data.build_id || data.version;
    
    // Initialize current version on first check
    if (!state.currentVersion) {
      state.currentVersion = localStorage.getItem('ep_active_version') || serverVersion;
      localStorage.setItem('ep_active_version', state.currentVersion);
    }
    
    // Check if new version available
    if (serverVersion && serverVersion !== state.currentVersion) {
      devLog.log('[SILENT_UPDATE V25.3.0] Update detected – waiting for safe activation', {
        current: state.currentVersion,
        new: serverVersion
      });
      
      state.updateReady = true;
      state.newVersion = serverVersion;
      
      // Pre-cache new assets in background (non-blocking)
      prefetchNewAssets();
      
      // Try immediate activation if safe
      tryActivation('version_check');
    }
  } catch (error) {
    // Silent fail - don't interrupt user
    devLog.debug('[SILENT_UPDATE V25.3.0] Version check failed (silent):', error.message);
  }
};

/**
 * Prefetch new assets in background
 */
const prefetchNewAssets = async () => {
  try {
    // Prefetch main bundle chunks
    const prefetchUrls = [
      '/static/js/main.chunk.js',
      '/static/js/bundle.js',
      '/static/css/main.chunk.css',
    ];
    
    await Promise.allSettled(
      prefetchUrls.map(url => 
        fetch(url, { cache: 'reload' }).catch(() => {})
      )
    );
    
    devLog.debug('[SILENT_UPDATE V25.3.0] Assets prefetched in background');
  } catch (error) {
    // Silent fail
  }
};

// ═══════════════════════════════════════════════════════════════════════════════
// 2️⃣ SAFE ACTIVATION RULES
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Check if it's safe to activate the update
 */
export const isSafeToActivate = () => {
  // Rule 1: No active streaming session
  if (state.isStreaming) {
    devLog.debug('[SILENT_UPDATE V25.3.0] Activation blocked: streaming active');
    return false;
  }
  
  // Rule 2: No ongoing generation process
  if (state.isGenerating) {
    devLog.debug('[SILENT_UPDATE V25.3.0] Activation blocked: generation in progress');
    return false;
  }
  
  // Rule 3: No ongoing refine process
  if (state.isRefining) {
    devLog.debug('[SILENT_UPDATE V25.3.0] Activation blocked: refine in progress');
    return false;
  }
  
  // Rule 4: No pending API calls
  if (state.pendingApiCalls > 0) {
    devLog.debug('[SILENT_UPDATE V25.3.0] Activation blocked: pending API calls', state.pendingApiCalls);
    return false;
  }
  
  // Rule 5: Check if on result/streaming page
  const pathname = window.location.pathname;
  if (pathname.includes('/result') || pathname.includes('/streaming')) {
    // Only allow if idle for extended period
    const idleTime = Date.now() - state.lastActivity;
    if (idleTime < IDLE_TIMEOUT * 2) {
      devLog.debug('[SILENT_UPDATE V25.3.0] Activation blocked: on result page, not idle enough');
      return false;
    }
  }
  
  return true;
};

/**
 * Attempt to activate the update if safe
 */
export const tryActivation = (trigger) => {
  if (!state.updateReady || !state.newVersion) {
    return false;
  }
  
  if (state.activationAttempted) {
    return false;
  }
  
  if (!isSafeToActivate()) {
    devLog.debug('[SILENT_UPDATE V25.3.0] Activation deferred - not safe');
    return false;
  }
  
  devLog.log('[SILENT_UPDATE V25.3.0] Update activated on safe navigation', {
    trigger,
    version: state.newVersion
  });
  
  state.activationAttempted = true;
  activateUpdate();
  return true;
};

// ═══════════════════════════════════════════════════════════════════════════════
// 3️⃣ ACTIVATION METHOD (NO PAGE RELOAD)
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Activate the update WITHOUT page reload
 * Uses React root remount for seamless transition
 */
const activateUpdate = async () => {
  try {
    // Step 1: Activate new service worker (if available)
    if ('serviceWorker' in navigator) {
      const registration = await navigator.serviceWorker.getRegistration();
      if (registration?.waiting) {
        registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        devLog.debug('[SILENT_UPDATE V25.3.0] Service worker activated');
      }
    }
    
    // Step 2: Update version in storage
    localStorage.setItem('ep_active_version', state.newVersion);
    state.currentVersion = state.newVersion;
    
    // Step 3: Clear module cache for dynamic imports
    if (window.__webpack_require__?.c) {
      // Clear cached modules to force reload on next import
      const cache = window.__webpack_require__.c;
      Object.keys(cache).forEach(key => {
        if (key.includes('chunk') || key.includes('main')) {
          delete cache[key];
        }
      });
    }
    
    // Step 4: Trigger React root remount via global event
    window.dispatchEvent(new CustomEvent('ep_silent_update', {
      detail: { version: state.newVersion }
    }));
    
    // Step 5: Reset state
    state.updateReady = false;
    state.newVersion = null;
    state.activationAttempted = false;
    
    devLog.log('[SILENT_UPDATE V25.3.0] ✅ Update activated seamlessly');
    
  } catch (error) {
    // Error safety: Keep current version running, don't reload
    console.error('[SILENT_UPDATE V25.3.0] Activation failed (keeping current version):', error.message);
    state.activationAttempted = false;
  }
};

// ═══════════════════════════════════════════════════════════════════════════════
// 4️⃣ STREAMING PROTECTION
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Mark streaming as started - blocks all updates
 */
export const startStreaming = () => {
  state.isStreaming = true;
  state.lastActivity = Date.now();
  devLog.debug('[SILENT_UPDATE V25.3.0] Streaming started - updates blocked');
};

/**
 * Mark streaming as complete - allows updates
 */
export const endStreaming = () => {
  state.isStreaming = false;
  state.lastActivity = Date.now();
  devLog.debug('[SILENT_UPDATE V25.3.0] Streaming ended - checking for pending updates');
  
  // Try activation after streaming completes
  setTimeout(() => tryActivation('streaming_complete'), 1000);
};

/**
 * Mark generation as started
 */
export const startGeneration = () => {
  state.isGenerating = true;
  state.lastActivity = Date.now();
};

/**
 * Mark generation as complete
 */
export const endGeneration = () => {
  state.isGenerating = false;
  state.lastActivity = Date.now();
  setTimeout(() => tryActivation('generation_complete'), 1000);
};

/**
 * Mark refine as started
 */
export const startRefine = () => {
  state.isRefining = true;
  state.lastActivity = Date.now();
};

/**
 * Mark refine as complete
 */
export const endRefine = () => {
  state.isRefining = false;
  state.lastActivity = Date.now();
  setTimeout(() => tryActivation('refine_complete'), 1000);
};

/**
 * Track API call start
 */
export const apiCallStart = () => {
  state.pendingApiCalls++;
  state.lastActivity = Date.now();
};

/**
 * Track API call end
 */
export const apiCallEnd = () => {
  state.pendingApiCalls = Math.max(0, state.pendingApiCalls - 1);
  state.lastActivity = Date.now();
  
  if (state.pendingApiCalls === 0) {
    setTimeout(() => tryActivation('api_idle'), 500);
  }
};

// ═══════════════════════════════════════════════════════════════════════════════
// NAVIGATION & IDLE DETECTION
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Handle route navigation - safe point for activation
 */
export const onNavigate = (newPath) => {
  state.lastActivity = Date.now();
  
  // Navigation is a safe point for activation
  if (state.updateReady && isSafeToActivate()) {
    devLog.log('[SILENT_UPDATE V25.3.0] Safe navigation detected - activating update');
    tryActivation('navigation');
  }
};

/**
 * Handle user activity
 */
export const onActivity = () => {
  state.lastActivity = Date.now();
};

/**
 * Check for idle state
 */
const checkIdle = () => {
  const idleTime = Date.now() - state.lastActivity;
  
  if (idleTime >= IDLE_TIMEOUT && state.updateReady) {
    devLog.debug('[SILENT_UPDATE V25.3.0] Idle detected - attempting activation');
    tryActivation('idle');
  }
};

// ═══════════════════════════════════════════════════════════════════════════════
// INITIALIZATION
// ═══════════════════════════════════════════════════════════════════════════════

let initialized = false;
let versionCheckInterval = null;
let idleCheckInterval = null;

/**
 * Initialize the silent update manager
 */
export const initSilentUpdateManager = () => {
  if (initialized) return;
  initialized = true;
  
  devLog.log('[SILENT_UPDATE V25.3.0] ════════════════════════════════════════════════');
  devLog.log('[SILENT_UPDATE V25.5.2] Silent Update Manager initialized');
  devLog.log('[SILENT_UPDATE V25.5.2] NO RELOADS | NO BLINKING | NO INTERRUPTION');
  devLog.log('[SILENT_UPDATE V25.5.2] ════════════════════════════════════════════════');
  
  // V25.5.2: DO NOT register service workers - they cause stale content issues
  // Service workers cache JS bundles and prevent updates from reflecting after deployment
  if ('serviceWorker' in navigator) {
    // UNREGISTER existing service workers instead of registering new ones
    navigator.serviceWorker.getRegistrations()
      .then(registrations => {
        registrations.forEach(registration => {
          devLog.log('[SILENT_UPDATE V25.5.2] Unregistering stale service worker:', registration.scope);
          registration.unregister();
        });
      })
      .catch(error => {
        devLog.log('[SILENT_UPDATE V25.5.2] SW unregistration failed (non-critical):', error.message);
      });
  }
  
  // Initial version check (delayed to not block app start)
  setTimeout(checkForUpdate, 5000);
  
  // Periodic version check
  versionCheckInterval = setInterval(checkForUpdate, VERSION_CHECK_INTERVAL);
  
  // Idle detection
  idleCheckInterval = setInterval(checkIdle, 10000);
  
  // Track user activity
  const activityEvents = ['mousedown', 'keydown', 'touchstart', 'scroll'];
  activityEvents.forEach(event => {
    document.addEventListener(event, onActivity, { passive: true });
  });
  
  // Track visibility changes (tab focus)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      onActivity();
      // Check for updates when tab becomes visible
      checkForUpdate();
    }
  });
  
  // Listen for navigation events
  window.addEventListener('popstate', () => onNavigate(window.location.pathname));
  
  // Cleanup on unload
  window.addEventListener('beforeunload', () => {
    if (versionCheckInterval) clearInterval(versionCheckInterval);
    if (idleCheckInterval) clearInterval(idleCheckInterval);
  });
};

/**
 * Get current state (for debugging)
 */
export const getUpdateState = () => ({ ...state });

// Export for use in components
export default {
  initSilentUpdateManager,
  checkForUpdate,
  isSafeToActivate,
  tryActivation,
  startStreaming,
  endStreaming,
  startGeneration,
  endGeneration,
  startRefine,
  endRefine,
  apiCallStart,
  apiCallEnd,
  onNavigate,
  onActivity,
  getUpdateState,
};
